/* eslint-disable camelcase */

import React, { useEffect, useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import {
  HvFileUploader,
  File,
  HvWarningText,
  HvInfoMessage,
} from "@hitachivantara/uikit-react-core";
import { useFormikContext } from "formik";

import { setId } from "lib/utils/ids";
import { parseStatus as ps, parseStatusMessage as psm } from "lib/utils/forms";
import { IncidentType } from "lib/api";
import { uploadAttachment } from "lib/api/attachments";

import useStyles from "./styles";

type FileWithId = File & {
  id: string;
};

type IconFileUploadProps = {
  onFileUpload: (uploading: boolean) => void;
};

const IconFileUpload: React.FC<IconFileUploadProps> = ({ onFileUpload }) => {
  const classes = useStyles();
  const { t } = useTranslation("addEditIncidentType");
  const [list, setList] = useState<FileWithId[]>([]);

  const {
    values: {
      appearance: { color, icon = "", icon_type, icon_attachment = "" } = {},
    } = {},
    errors,
    touched,
    status: { serverSideErrors } = {},
    setFieldValue,
    setFieldTouched,
    setStatus,
  } = useFormikContext<Partial<IncidentType>>();

  const parseStatus = useCallback(
    (field: string) => ps(field, serverSideErrors, errors, touched),
    [errors, serverSideErrors, touched]
  );

  const parseStatusMessage = useCallback(
    (field: string) => psm(field, serverSideErrors, errors, touched),
    [errors, serverSideErrors, touched]
  );

  const disabled = useMemo(
    () => icon_type !== "attachment_id" || list.length === 1,
    [icon_type, list]
  );

  const addFile = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append(file.name, file);
      if (file.status === "fail") {
        setStatus({
          serverSideErrors: {
            ...serverSideErrors,
            appearance: {
              icon: file.errorMessage,
            },
          },
        });
        return;
      }
      onFileUpload(true);
      uploadAttachment(formData)
        .then((response) => {
          onFileUpload(false);
          setFieldValue("appearance", {
            color,
            icon: response.data.data.attachment_id,
            icon_type,
            icon_attachment: `attachment_id|${file.name}`,
          });
          setFieldTouched("appearance.icon", true, false);
          setFieldTouched("appearance.icon_attachment", true, false);
          setStatus({
            serverSideErrors: {
              ...serverSideErrors,
              appearance: {
                icon: undefined,
              },
            },
          });
        })
        .catch(() => {
          onFileUpload(false);
          setStatus({
            serverSideErrors: {
              appearance: {
                icon: t(`validation.errorUploadingIcon`),
              },
            },
          });
        });
    },
    [
      onFileUpload,
      setFieldValue,
      color,
      icon_type,
      setFieldTouched,
      setStatus,
      serverSideErrors,
      t,
    ]
  );

  /* commented for the release 7.0
  const removeFile = useCallback(async () => {
    deleteAttachment(list[0].id)
      .then(() => {
        setFieldValue("appearance", {
          color,
          icon: null,
          icon_type,
          icon_attachment: null,
        });
      })
      .catch(() => {
        setStatus({
          serverSideErrors: {
            appearance: {
              icon: t(`validation.errorDeletingIcon`),
            },
          },
        });
      });
  }, [color, icon_type, list, setFieldValue, setStatus, t]); */
  const removeFile = useCallback(async () => {
    setFieldValue("appearance", {
      color,
      icon: null,
      icon_type,
      icon_attachment: null,
    });
  }, [color, icon_type, setFieldValue]);

  const onFilesAdded = (newFiles: File[]) => {
    newFiles.forEach((newFile) => addFile(newFile));
  };

  useEffect(() => {
    if (icon_type !== "attachment_id" && list.length === 1) {
      removeFile().then(() => setList([]));
    } else if (icon_type === "attachment_id" && list.length === 1 && !icon) {
      setList([]);
    } else if (icon_type === "attachment_id" && list.length === 0 && !!icon) {
      const name = icon_attachment?.split("|")[1] as string;
      setList([
        {
          id: icon as string,
          name,
          status: "success",
        },
      ]);
    }
  }, [icon, icon_attachment, icon_type, list.length, removeFile]);

  const id = "iconFileUpload";

  return (
    <div className={classes.fileUploader}>
      <HvInfoMessage
        id={setId(id, "description")}
        className={classes.description}
        disabled={icon_type !== "attachment_id"}
      >
        {t("mapMarker.iconType.options.uploadIcon.description")}
      </HvInfoMessage>
      <HvFileUploader
        id={id}
        className={clsx({
          [classes.hideAfterOne]: list.length === 1,
        })}
        fileList={list}
        acceptedFiles={["image/*"]}
        maxFileSize={500000}
        multiple={false}
        disabled={disabled}
        hideLabels
        inputProps={{
          "aria-labelledby": "uploadIconRadio-label",
          "aria-describedby": setId(id, "description"),
        }}
        labels={{
          fileSizeError: t("validation.iconFileSizeNotValid"),
          fileTypeError: t("validation.iconFileTypeNotValid"),
          drag: t("mapMarker.iconType.options.uploadIcon.drag"),
          dropFiles: t("mapMarker.iconType.options.uploadIcon.dropFiles"),
          selectFiles: t("mapMarker.iconType.options.uploadIcon.selectFiles"),
          removeFileButtonLabel: t(
            "mapMarker.iconType.options.uploadIcon.removeFileButtonLabel"
          ),
        }}
        onFilesAdded={(newFiles) => onFilesAdded(newFiles)}
        onFileRemoved={removeFile}
      />
      {!disabled && parseStatus("appearance.icon") !== "valid" && (
        <HvWarningText id="iconFileUpload-error" disableBorder isVisible>
          {parseStatusMessage("appearance.icon")}
        </HvWarningText>
      )}
    </div>
  );
};

export default IconFileUpload;
